import { Link, useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Contest, PVExtracted } from '@anuleaza/shared';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo, MonoLabel } from '../components/primitives/index.js';
import { ExtractedFieldList } from '../components/composites/index.js';

export function ContestConfirm({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const contest = useQuery({
    queryKey: ['contest', id],
    queryFn: () => api.contest(id),
    enabled: !!me.data,
  });

  // Optimistic patch: replace the contest in cache immediately, then send the
  // request. If it fails, we roll back and toast. The server returns the
  // canonical updated contest, which overwrites our optimistic state.
  const patch = useMutation({
    mutationFn: (extracted: PVExtracted) => api.patchExtracted(id, extracted),
    onMutate: async (extracted) => {
      await qc.cancelQueries({ queryKey: ['contest', id] });
      const prev = qc.getQueryData<{ contest: Contest }>(['contest', id]);
      if (prev) {
        qc.setQueryData(['contest', id], {
          contest: { ...prev.contest, extracted_json: extracted },
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['contest', id], ctx.prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(['contest', id], data);
    },
  });

  if (!me.data || contest.isLoading) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  const c = contest.data?.contest;
  if (!c || !c.extracted_json) {
    return (
      <Shell>
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-xl font-medium mb-2">Date indisponibile</h1>
          <p className="text-muted text-sm mb-6">
            Procesarea încă nu s-a încheiat sau a eșuat. Încearcă din nou.
          </p>
          <Link href="/dashboard">
            <Button variant="ghost" pill icon={false}>← Înapoi la dashboard</Button>
          </Link>
        </Card>
      </Shell>
    );
  }

  const wasAnalyzed = c.status === 'analyzed';

  return (
    <Shell>
      <div className="max-w-3xl mx-auto">
        <MonoLabel>Pasul 3 · confirmă datele</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium mt-3 mb-2">Asta a citit AI-ul.</h1>
        <p className="text-muted leading-relaxed mb-8 max-w-2xl">
          Verifică câmpurile extrase din PV. <strong className="text-fg">Apasă pe orice câmp</strong>{' '}
          pentru a-l corecta — Enter sau click în afară pentru a salva, Esc pentru a anula.
          Câmpurile marcate cu <span className="text-accent-dark">•</span> sunt cele mai importante
          pentru analiza juridică.
        </p>

        {patch.isError && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-danger/10 border border-danger/30 text-sm text-danger">
            Modificarea n-a putut fi salvată. Verifică conexiunea și reîncearcă.
          </div>
        )}

        {wasAnalyzed && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-warn-soft border border-warn/40 text-sm text-warn-text">
            <strong>Notă:</strong> Acest dosar are deja analiză juridică. Dacă modifici datele,
            analiza se resetează și va trebui rulată din nou.
          </div>
        )}

        <ExtractedFieldList
          extracted={c.extracted_json}
          confidence={c.ocr_confidence ?? 0}
          onChange={(next) => patch.mutate(next)}
          disabled={patch.isPending}
        />

        <div className="mt-8 flex flex-wrap gap-3 justify-between items-center">
          <Button
            variant="ghost"
            pill
            icon={false}
            onClick={() => setLocation('/dashboard')}
          >
            ← La dashboard
          </Button>
          <Button
            pill
            onClick={() => setLocation(`/contest/${id}/questions`)}
            disabled={!c.extracted_json || patch.isPending}
          >
            Continuă cu analiza
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-6 md:py-10">{children}</main>
    </div>
  );
}
